/**
 * IndexedDB de la caja offline. Tres almacenes, con dueños distintos:
 *
 * - `responses`: copia del catálogo para abrir y dibujar la app sin red (offlineCache).
 * - `queue`: operaciones hechas sin red, pendientes de sincronizar (offlineQueue).
 * - `orders`: cómo van las órdenes que la caja abrió o modificó sin red, para que las
 *   pantallas las vean como si vinieran del servidor (offlineOrders).
 *
 * Todo se guarda con lo que ya trae el navegador, sin librería: son tres almacenes de
 * clave-valor y no vale traerse Dexie para eso.
 */
export const DB_NAME = 'massrestaurant-offline';
export const DB_VERSION = 2;

export const STORE_RESPONSES = 'responses';
export const STORE_QUEUE = 'queue';
export const STORE_ORDERS = 'orders';

let dbPromise: Promise<IDBDatabase> | null = null;

export const openOfflineDb = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        // Sin `contains` una versión nueva truena al reabrir una base ya creada.
        if (!db.objectStoreNames.contains(STORE_RESPONSES)) {
          db.createObjectStore(STORE_RESPONSES, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORE_QUEUE)) {
          db.createObjectStore(STORE_QUEUE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_ORDERS)) {
          db.createObjectStore(STORE_ORDERS, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    // Si falla una vez (modo privado, cuota), se reintenta en la siguiente llamada.
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
};

const run = <T>(store: string, mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest<T>) =>
  openOfflineDb().then((db) => new Promise<T>((resolve, reject) => {
    const request = work(db.transaction(store, mode).objectStore(store));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }));

export const idbGet = <T>(store: string, key: string): Promise<T | undefined> =>
  run<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);

export const idbGetAll = <T>(store: string): Promise<T[]> =>
  run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);

export const idbPut = (store: string, value: unknown): Promise<unknown> =>
  run(store, 'readwrite', (s) => s.put(value as any));

export const idbDelete = (store: string, key: string): Promise<unknown> =>
  run(store, 'readwrite', (s) => s.delete(key));

export const resetOfflineDbForTests = (): void => { dbPromise = null; };
